import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { CreditCard, Save, Plus, Trash2, GripVertical, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPaymentMethods() {
  const { token } = useContext(AuthContext);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newMethodName, setNewMethodName] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { fetchMethods(); }, []);

  const fetchMethods = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/payment-methods`, authHeaders);
      setMethods(res.data.methods || []);
    } catch (err) {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/payment-methods`, { methods }, authHeaders);
      toast.success('Payment methods saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addMethod = () => {
    const name = newMethodName.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (methods.some(m => m.id === id)) {
      toast.error('Method already exists');
      return;
    }
    setMethods([...methods, { id, name, enabled: true, xero_account_code: '' }]);
    setNewMethodName('');
  };

  const removeMethod = (id) => {
    setMethods(methods.filter(m => m.id !== id));
  };

  const updateMethod = (id, field, value) => {
    setMethods(methods.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const moveMethod = (index, direction) => {
    const arr = [...methods];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setMethods(arr);
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="payment-methods-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight flex items-center gap-3">
              <CreditCard className="h-8 w-8 text-[#78BE20]" />
              Payment Methods
            </h1>
            <p className="text-zinc-400 mt-1">Configure accepted payment methods and Xero account mappings</p>
          </div>
          <Button onClick={saveAll} disabled={saving} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="save-methods-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-zinc-800 rounded animate-pulse" />)}</div>
        ) : (
          <>
            <Card className="bg-[#18181b] border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Active Methods</CardTitle>
                <CardDescription>
                  Drag to reorder. Assign Xero account codes so payments land in the correct bank account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {methods.length === 0 ? (
                  <p className="text-zinc-500 text-sm py-4 text-center">No payment methods configured</p>
                ) : (
                  methods.map((method, index) => (
                    <div key={method.id} className={`flex items-center gap-3 p-3 rounded-lg border ${method.enabled ? 'bg-zinc-900 border-zinc-800' : 'bg-zinc-900/50 border-zinc-800/50 opacity-60'}`} data-testid={`method-row-${method.id}`}>
                      <div className="flex flex-col gap-0.5">
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-zinc-500 hover:text-white" onClick={() => moveMethod(index, -1)} disabled={index === 0}>
                          <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor"><path d="M5 0L10 6H0L5 0Z"/></svg>
                        </Button>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-zinc-500 hover:text-white" onClick={() => moveMethod(index, 1)} disabled={index === methods.length - 1}>
                          <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0H10L5 6Z"/></svg>
                        </Button>
                      </div>

                      <Switch checked={method.enabled} onCheckedChange={(v) => updateMethod(method.id, 'enabled', v)} data-testid={`toggle-${method.id}`} />

                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <Input
                            value={method.name}
                            onChange={(e) => updateMethod(method.id, 'name', e.target.value)}
                            className="bg-zinc-800 border-zinc-700 h-9 text-sm"
                            placeholder="Method name"
                            data-testid={`name-${method.id}`}
                          />
                        </div>
                        <div>
                          <Input
                            value={method.xero_account_code || ''}
                            onChange={(e) => updateMethod(method.id, 'xero_account_code', e.target.value)}
                            className="bg-zinc-800 border-zinc-700 h-9 text-sm font-mono"
                            placeholder="Xero Account Code (optional)"
                            data-testid={`xero-code-${method.id}`}
                          />
                        </div>
                      </div>

                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400" onClick={() => removeMethod(method.id)} data-testid={`delete-${method.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Add Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    value={newMethodName}
                    onChange={(e) => setNewMethodName(e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="e.g. Afterpay, PayPal, Crypto"
                    onKeyDown={(e) => e.key === 'Enter' && addMethod()}
                    data-testid="new-method-input"
                  />
                  <Button onClick={addMethod} className="bg-zinc-700 hover:bg-zinc-600 shrink-0" data-testid="add-method-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-400">
                <strong>Xero Integration:</strong> When a payment is recorded against an order or invoice, the system will use the mapped Xero Account Code to record the payment in the correct bank account in Xero. Leave blank to use Xero's default bank account.
              </p>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
